import { PATH } from '@/app/routes';
import { ErrorFallback } from './ErrorFallback';
import { getFaroInstance } from '@/app/config/logging';
import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  private faro = getFaroInstance();

  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  private logErrorDetails(error: Error, errorInfo: ErrorInfo, context: Record<string, string> = {}) {
    // Log detailed error information
    this.faro.api.pushError(error, {
      context: {
        component: 'ErrorBoundary',
        componentStack: errorInfo.componentStack || 'No component stack available',
        ...this.getErrorMetadata(error),
        ...context,
      },
    });

    // Log as structured event
    this.faro.api.pushLog([{
      level: 'error',
      message: `React Error Boundary Caught: ${error.message}`,
      context: {
        component: 'ErrorBoundary',
        errorName: error.name,
        errorStack: error.stack || 'No stack trace available',
        componentStack: errorInfo.componentStack || 'No component stack available',
        ...this.getErrorMetadata(error),
        ...context,
      },
    }]);
  }

  private getErrorMetadata(error: Error): Record<string, string> {
    const metadata: Record<string, string> = {
      errorType: error.constructor.name,
      timestamp: new Date().toISOString(),
    };

    // Extract additional error properties
    if (error instanceof TypeError) {
      metadata.category = 'type-error';
    } else if (error instanceof ReferenceError) {
      metadata.category = 'reference-error';
    } else if (error instanceof SyntaxError) {
      metadata.category = 'syntax-error';
    } else if (error instanceof RangeError) {
      metadata.category = 'range-error';
    } else {
      metadata.category = 'unknown-error';
    }

    // Check for invalid React child errors
    if (error.message.includes('Objects are not valid as a React child')) {
      metadata.category = 'invalid-react-child';
      const match = error.message.match(/found: (.+?)\)/);
      if (match) {
        metadata.invalidObject = match[1];
      }
    }

    return metadata;
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.logErrorDetails(error, errorInfo);
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleRetry = () => {
    this.faro.api.pushLog([{
      level: 'info',
      message: 'User triggered page reload from error boundary',
      context: {
        component: 'ErrorBoundary',
        action: 'retry',
        error: this.state.error?.message,
      },
    }]);
    window.location.reload();
  };

  private handleNavigateToCatalog = () => {
    this.faro.api.pushLog([{
      level: 'info',
      message: 'User navigated to catalog from error boundary',
      context: {
        component: 'ErrorBoundary',
        action: 'navigate_to_catalog',
        error: this.state.error?.message,
      },
    }]);
    window.location.href = PATH.catalog;
  };

  public render() {
    if (this.state.hasError) {
      // Log render of error UI
      this.faro.api.pushLog([{
        level: 'info',
        message: 'Rendering error boundary fallback UI',
        context: {
          component: 'ErrorBoundary',
          errorMessage: this.state.error?.message || 'Unknown error',
        },
      }]);

      return <ErrorFallback onRetry={this.handleRetry} onNavigateToCatalog={this.handleNavigateToCatalog} />;
    }

    return this.props.children;
  }
}
